import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePremadeQuizzes } from '@quizzach/api-hooks';
import { Search } from '@quizzach/assets';
import {
	Navigation,
	PremadeQuizCardComponent,
	CardAuthorMark,
	CollabStrip,
	QuickAddButton,
	PremadeLoadingSkeleton,
} from '@quizzach/components';
import { IPremadeQuiz, RoutePath } from '@quizzach/interfaces';
import { useQuizOwnershipContext } from '@quizzach/contexts';
import { Reveal, useParallax } from '@quizzach/motion';
import {
	premadeLevelKey,
	scrollToTop,
	sortBySectionOrder,
	sortQuizzesNewestFirst,
} from '@quizzach/utils';
import {
	CardsGrid,
	ChapterBand,
	Chapters,
	Fc,
	Izlog,
	PhHead,
	PremadePage,
	Sk,
	TagBar,
} from '@quizzach/styles';

/** Cards per chapter row; collapsed chapters show one row (3 on mobile). */
const ROW_SIZE = 4;

// Chapter definitions; tag names double as the /premade/tag/:tag routes.
const CHAPTER_DEFS = [
	{ key: 'film', tag: 'film&tv', cls: 'c-film' },
	{ key: 'sport', tag: 'sport', cls: 'c-sport' },
	{ key: 'put', tag: 'put oko svijeta', cls: 'c-put' },
	{ key: 'muz', tag: 'glazba', cls: 'c-muz' },
	{ key: 'pov', tag: 'poveznica', cls: 'c-pov' },
];

const formatPrice = (price?: string) =>
	price?.replace('.00', '').replace('.', ',');

const hasTag = (quiz: IPremadeQuiz, tag: string) =>
	(quiz.primary_thematic_tags ?? []).some((t) => t.name === tag);

/**
 * One izlog cover as a sale ticket: the photo runs to the coupon
 * perforation and carries the title and mono meta row itself (text
 * shadows plus a quiet plate gradient instead of the old full shade),
 * price and author signature sit on the paper coupon behind the bottom
 * perforation — the same direction as every other ticket; the big card
 * is just the grande print of it.
 */
const FeaturedCard = ({ quiz, big }: { quiz: IPremadeQuiz; big?: boolean }) => {
	const { t } = useTranslation();
	const imgRef = useRef<HTMLImageElement>(null);

	// the big cover scroll-parallaxes like the homepage hero photo (gentle)
	useParallax(imgRef, 0.05);

	const levelKey = premadeLevelKey(quiz.level);

	return (
		<Fc
			as={Link}
			className={big ? 'big' : 'small'}
			to={`${RoutePath.PremadeQuizzes}/quiz/${quiz.uuid}`}
			onClick={scrollToTop}
		>
			<span className='ph' data-darkzone>
				<img
					ref={big ? imgRef : undefined}
					className='bg'
					src={`${import.meta.env.VITE_ASSETS_URL}/thematic_quizzes/${
						big ? quiz.cover_photo : quiz.cover_photo_small
					}`}
					alt={quiz.title}
				/>
				<QuickAddButton quiz={quiz} />
				<span className='plate'>
					<h3>{quiz.title}</h3>
					<span className='meta'>
						{levelKey && `${t(levelKey)} · `}
						{t('premade.card.questionCount', {
							count: quiz.number_of_questions - 1,
						})}
					</span>
				</span>
			</span>
			<span className='holes' aria-hidden='true' />
			<span className='stub'>
				<span className='pr'>
					<span className='lb'>{t('premade.card.priceLabel')}</span>
					<span className='eur'>{formatPrice(quiz.price)} EUR</span>
				</span>
				<span className='by'>
					<CardAuthorMark displayName={quiz.author?.display_name} />
				</span>
			</span>
		</Fc>
	);
};

/**
 * One category band. Shows a single row collapsed with a "još ovakvih!"
 * expand pill; `showAll` (the catch-all) lists everything at once on every
 * viewport, no pill.
 */
const Chapter = ({
	cls,
	title,
	hand,
	quizzes,
	showAll,
}: {
	cls: string;
	title: string;
	hand: string;
	quizzes: IPremadeQuiz[];
	showAll?: boolean;
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const shown = showAll || open ? quizzes : quizzes.slice(0, ROW_SIZE);
	const cn = [cls, open && 'open', showAll && 'all'].filter(Boolean).join(' ');

	return (
		<ChapterBand className={cn}>
			<span className='side' aria-hidden='true'>
				<span>{title}</span>
			</span>
			<Reveal className='chead'>
				<h3>{title}</h3>
				<span className='hand'>{hand}</span>
			</Reveal>
			{/* one quiet motion per band: the grid reveals as a single block
			    (per-card staggering read as too busy) */}
			<Reveal className='cgrid' delay={1}>
				{shown.map((quiz) => (
					<PremadeQuizCardComponent key={quiz.uuid} quiz={quiz} />
				))}
			</Reveal>
			{!showAll && quizzes.length > ROW_SIZE && (
				<button
					type='button'
					className='exp'
					onClick={() => setOpen((o) => !o)}
				>
					{open ? t('premade.chapters.less') : t('premade.chapters.more')}
				</button>
			)}
		</ChapterBand>
	);
};

/**
 * Posebna izdanja: featured izlog (one big + two small hot covers), then
 * one full-bleed band per category (one row + expand), then a catch-all
 * that lists every quiz not visible in the rows above.
 */
export const PremadeQuizzes = () => {
	const { t } = useTranslation();
	const { premadeQuizzes, loading, getPremadeQuizzes } = usePremadeQuizzes();
	const { uuidSet: ownedUuids } = useQuizOwnershipContext();
	const hasLoadedRef = useRef(false);

	useEffect(() => {
		if (hasLoadedRef.current) return;
		hasLoadedRef.current = true;
		getPremadeQuizzes();
	}, [getPremadeQuizzes]);

	const { featured, novo, chapters, catchAll } = useMemo(() => {
		const all = premadeQuizzes ?? [];
		// izlog: the top three of the 'hot' section, in their admin order
		const hot = all.filter((q) => q.section === 'hot').sort(sortBySectionOrder);
		const featuredList = hot.slice(0, 3);

		// novo band: the 'new' section in its admin order; like the izlog it
		// doesn't claim quizzes, so they still appear in their tag band below
		const novoList = all
			.filter((q) => q.section === 'new')
			.sort(sortBySectionOrder);

		// first matching tag wins so each quiz lives in exactly one band;
		// hot quizzes lead their band, the rest follows newest-first — but
		// quizzes already in the profile collection sink to the band's tail so
		// the collapsed row leads with tickets the user can still buy
		const taken = new Set<string>();
		const byChapter = CHAPTER_DEFS.map((def) => {
			const inCat = all.filter((q) => !taken.has(q.uuid) && hasTag(q, def.tag));
			inCat.forEach((q) => taken.add(q.uuid));
			const ordered = [
				...inCat.filter((q) => q.section === 'hot').sort(sortBySectionOrder),
				...sortQuizzesNewestFirst(inCat.filter((q) => q.section !== 'hot')),
			];
			const quizzes = [
				...ordered.filter((q) => !ownedUuids.has(q.uuid)),
				...ordered.filter((q) => ownedUuids.has(q.uuid)),
			];
			return { ...def, quizzes };
		});

		// catch-all = quizzes without a chapter + everything hidden behind the
		// expand pills, so plain scrolling reaches the whole collection
		const uncategorized = sortQuizzesNewestFirst(
			all.filter((q) => !taken.has(q.uuid))
		);
		const overflow = byChapter.flatMap((c) => c.quizzes.slice(ROW_SIZE));

		return {
			featured: featuredList,
			novo: novoList,
			chapters: byChapter,
			catchAll: [...uncategorized, ...overflow],
		};
	}, [premadeQuizzes, ownedUuids]);

	const title = t('homepage.premade.title');
	const splitAt = title.lastIndexOf(' ');
	const titlePre = splitAt === -1 ? title : title.slice(0, splitAt);
	const titleAccent = splitAt === -1 ? '' : title.slice(splitAt + 1);

	return (
		<PremadePage>
			<Navigation />

			<PhHead>
				{/* homepage-hero treatment: title lines rise out of an overflow
				    clip on load (styles live on PhHead) */}
				<h1>
					<span className='row'>
						<span>{titlePre}</span>
					</span>
					{titleAccent && (
						<span className='row'>
							<span>
								<em>{titleAccent}</em>
							</span>
						</span>
					)}
				</h1>
				<Reveal className='side' delay={1}>
					<p>{t('homepage.premade.description')}</p>
					<span className='hand'>{t('premade.redesign.sideHand')}</span>
				</Reveal>
			</PhHead>

			<Reveal>
				<TagBar>
					{CHAPTER_DEFS.map(({ tag }) => (
						<Link key={tag} to={`${RoutePath.PremadeQuizzes}/tag/${tag}`}>
							{tag}
						</Link>
					))}
					<Link
						className='search-btn'
						to={`${RoutePath.PremadeQuizzes}/search`}
					>
						<Search size={16} />
						{/* label hides on a phone (styles), where the icon
						    alone carries the button — kept in the DOM so the
						    link still announces itself */}
						<span className='lbl'>{t('premade.search.link')}</span>
					</Link>
				</TagBar>
			</Reveal>

			{featured.length > 0 ? (
				<Reveal>
					<Izlog>
						{featured.map((quiz, i) => (
							<FeaturedCard key={quiz.uuid} quiz={quiz} big={i === 0} />
						))}
					</Izlog>
				</Reveal>
			) : (
				loading && (
					<Izlog aria-hidden='true'>
						<Sk style={{ gridRow: '1 / 3', borderRadius: 18 }} />
						<Sk style={{ borderRadius: 18 }} />
						<Sk style={{ borderRadius: 18 }} />
					</Izlog>
				)
			)}

			<CollabStrip quizzes={premadeQuizzes ?? []} />

			{loading && !premadeQuizzes ? (
				<CardsGrid>
					<PremadeLoadingSkeleton numberOfQuizzes={8} />
				</CardsGrid>
			) : (
				<Chapters>
					{novo.length > 0 && (
						<Chapter
							cls='c-novo'
							title={t('premade.chapters.novo')}
							hand={t('premade.chapters.novoHand')}
							quizzes={novo}
						/>
					)}
					{chapters
						.filter((c) => c.quizzes.length > 0)
						.map((c) => (
							<Chapter
								key={c.key}
								cls={c.cls}
								title={t(`premade.chapters.${c.key}`)}
								hand={t(`premade.chapters.${c.key}Hand`)}
								quizzes={c.quizzes}
							/>
						))}
					{catchAll.length > 0 && (
						<Chapter
							cls='c-rest'
							title={t('premade.chapters.catchAll')}
							hand={t('premade.chapters.catchAllHand')}
							quizzes={catchAll}
							showAll
						/>
					)}
				</Chapters>
			)}
		</PremadePage>
	);
};
